import type { ComponentType, SVGProps } from 'react';

/**
 * Landing channel icon set — the 7 channels routines reach members on today:
 * phone call, SMS, WhatsApp, email, Slack, web call (room), calendar.
 *
 * Dependency-free inline SVGs (no lucide) so the design system can mirror the
 * exact glyphs. All icons: 24×24 viewBox, stroke=currentColor, strokeWidth 1.8,
 * round caps/joins. The WhatsApp and Slack glyphs are original abstractions
 * (chat-bubble-with-handset / four-rounded-rects pinwheel), NOT the trademarks.
 */

export type ChannelIconProps = SVGProps<SVGSVGElement>;

const baseProps = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

export function PhoneIcon(props: ChannelIconProps) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

export function SmsIcon(props: ChannelIconProps) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M8 10h.01" />
      <path d="M12 10h.01" />
      <path d="M16 10h.01" />
    </svg>
  );
}

export function WhatsappIcon(props: ChannelIconProps) {
  return (
    <svg {...baseProps} {...props}>
      {/* Round chat bubble with tail */}
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      {/* Miniature handset inside the bubble (strokeWidth 5 × scale 0.36 = 1.8 effective) */}
      <g transform="translate(7.6 7.4) scale(0.36)">
        <path
          strokeWidth={5}
          d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
        />
      </g>
    </svg>
  );
}

export function EmailIcon(props: ChannelIconProps) {
  return (
    <svg {...baseProps} {...props}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

export function SlackIcon(props: ChannelIconProps) {
  return (
    <svg {...baseProps} {...props}>
      {/* Four rounded rects in a pinwheel */}
      <rect x="12.8" y="2.8" width="4" height="8.4" rx="2" />
      <rect x="12.8" y="2.8" width="4" height="8.4" rx="2" transform="rotate(90 12 12)" />
      <rect x="12.8" y="2.8" width="4" height="8.4" rx="2" transform="rotate(180 12 12)" />
      <rect x="12.8" y="2.8" width="4" height="8.4" rx="2" transform="rotate(270 12 12)" />
    </svg>
  );
}

export function WebCallIcon(props: ChannelIconProps) {
  return (
    <svg {...baseProps} {...props}>
      {/* Camera body + right-pointing lens triangle */}
      <rect x="2" y="6" width="14" height="12" rx="2.5" />
      <path d="m22 8.2-6 3.8 6 3.8V8.2z" />
    </svg>
  );
}

export function CalendarIcon(props: ChannelIconProps) {
  return (
    <svg {...baseProps} {...props}>
      {/* Calendar body, two top pins, header rule, grid dots */}
      <rect x="3" y="4.5" width="18" height="17" rx="2" />
      <path d="M8 2.5v4" />
      <path d="M16 2.5v4" />
      <path d="M3 9.5h18" />
      <path d="M8 14.5h.01" />
      <path d="M12 14.5h.01" />
      <path d="M16 14.5h.01" />
    </svg>
  );
}

export interface ChannelMeta {
  key: 'phone' | 'sms' | 'whatsapp' | 'email' | 'slack' | 'webcall' | 'calendar';
  label: string;
  Icon: ComponentType<ChannelIconProps>;
  /** Icon / accent color */
  accent: string;
  /** Pastel chip background the icon sits on */
  chipBg: string;
}

export const CHANNEL_META: ChannelMeta[] = [
  { key: 'phone', label: 'Phone call', Icon: PhoneIcon, accent: '#10B981', chipBg: '#ECFDF5' },
  { key: 'sms', label: 'SMS', Icon: SmsIcon, accent: '#3B82F6', chipBg: '#EFF6FF' },
  { key: 'whatsapp', label: 'WhatsApp', Icon: WhatsappIcon, accent: '#22C55E', chipBg: '#F0FDF4' },
  { key: 'email', label: 'Email', Icon: EmailIcon, accent: '#F97316', chipBg: '#FFF7ED' },
  { key: 'slack', label: 'Slack', Icon: SlackIcon, accent: '#8B5CF6', chipBg: '#F5F3FF' },
  { key: 'webcall', label: 'Web call', Icon: WebCallIcon, accent: '#EC4899', chipBg: '#FDF2F8' },
  { key: 'calendar', label: 'Calendar', Icon: CalendarIcon, accent: '#EAB308', chipBg: '#FEFCE8' },
];
